// Production-eligible replay recorder. Records every merged-inputs tick into
// an in-memory MutableReplay; finalizable on demand (for the F9 dev overlay)
// or automatically on MatchEnded (for CPU-vs-CPU auto-export).
//
// Used by App directly (no dev gate). The dev-only ReplayController in
// debug/ reads from this via app.getRecorder() rather than maintaining its
// own parallel recording state.

import type { PlayerInput } from "@arcade-hockey/shared";
import {
  type MatchState,
  type MutableReplay,
  type Replay,
  type RosterSizes,
  emptyReplay,
  finalizeReplay,
  recordTick,
} from "@arcade-hockey/sim";

export class ReplayRecorder {
  private recorder: MutableReplay;

  constructor(seed: number, rosterSizes?: RosterSizes) {
    this.recorder = emptyReplay(seed, "", rosterSizes);
  }

  // Append a tick's merged inputs. Caller passes in the read-only inputs
  // buffer it just fed to tickMatch.
  onTick(tick: number, mergedInputs: ReadonlyArray<PlayerInput>): void {
    recordTick(this.recorder, tick, mergedInputs);
  }

  // Start a fresh recording at a new seed. Used when restarting a match.
  // `rosterSizes` is stamped onto the new MutableReplay so a finalized
  // capture carries the roster the match actually ran at.
  reset(seed: number, rosterSizes?: RosterSizes): void {
    this.recorder = emptyReplay(seed, "", rosterSizes);
  }

  // Snapshot the in-flight recording as a finalized Replay. Does NOT clear
  // the underlying MutableReplay; the recording continues afterward.
  // Returns a fresh Replay each call (with stamped recordedAt/finalHash).
  capture(state: MatchState, description = ""): Replay {
    return finalizeReplay(this.recorder, state, description || this.recorder.description);
  }

  getMutable(): MutableReplay {
    return this.recorder;
  }
}
